import { GridColDef, GridRowId } from '@mui/x-data-grid-premium';
import { useLayoutEffect, useMemo, useState } from 'react';
import useUpdateEffect from "src/hooks/useUpdateEffect";
import { tableSettingsProps } from './useTableSettings';

/* ---------- helpers ---------- */
export type MergeResult<Row> =
  | Row[]
  | {
      rows: Row[];
      message?: string;
      keepOpen?: boolean;
    };

/** Options accepted by `useTableSelector`.  
 *  Only `rows`, `setRows`, `selectGrid` and `getId` are required;
 *  everything else can be overridden piecemeal later.
 */
export interface UseTableSelectorOptions<Row> {
  rows: Row[];
  setRows: React.Dispatch<React.SetStateAction<Row[]>>;
  selectGrid: [GridColDef[] | any, tableSettingsProps];
  getId: (row: Row) => string | number;

  /** For merging picked rows */
  onMerge?: (
    prev: Row[],
    picked: Row[],
    replaceId?: string | number | null,
  ) => MergeResult<Row>;

  rowId?: string;  
  displayTableSettings?: tableSettingsProps;
  onCommitFunction?: (updated: Row, original: Row) => Partial<Row>;
}

/** Returned API from the hook. Suitable to spread directly into your dialog. */
export interface TableSelector<Row> {
  open: boolean;
  onClose(): void;
  /** Optional opener that lets you pass an id for replace-on-pick flows. */
  openSelector(id?: string | number | null): void;

  /** Imperative action to merge the currently checked rows (checkbox flow). */
  applySelection(opts?: { keepOpen?: boolean }): void;

  /** Convenience for UI */
  selectedRows: Row[];
  selectedCount: number;
  clearSelection(): void;

  columns: GridColDef[] | any[];
  tableSettings: tableSettingsProps;
  message?: string;
  clearMessage?: () => void;
}

/* ---------- hook ---------- */
export const useTableSelector = <Row,>({
  rows,
  setRows,
  selectGrid,
  getId,
  onMerge,
  rowId = "Id",
  displayTableSettings,
  onCommitFunction,
}: UseTableSelectorOptions<Row>): TableSelector<Row> => {
  const [selectorOpen, setSelectorOpen] = useState(false);
  const [replaceId, setReplaceId] = useState<string | number | null>(null);
  const [message, setMessage] = useState<string>();

  const [selColumns, selSettings] = selectGrid;

  /* -------- default merge -------- */
  const defaultMerge = (
    prev: Row[],
    picked: Row[],
    id?: string | number | null,
  ): Row[] => {
    let next = id != null ? prev.filter(r => getId(r) !== id) : [...prev];
    picked.forEach(r => {
      if (!next.some(p => getId(p) === getId(r))) next.push(r);
    });
    return next;
  };

  const mergeImpl = onMerge ?? defaultMerge;

  /* -------- compute selected rows (memoized) -------- */
  const selectedRows = useMemo(() => {
    const model = selSettings.rowSelectionModel ?? [];
    const all = selSettings.allRows ?? [];
    if (!Array.isArray(model) || !Array.isArray(all)) return [] as Row[];

    const picked: Row[] = [];
    const set = new Set(model as GridRowId[]);
    for (const r of all) {
      const id = getId(r);
      if (set.has(id as GridRowId)) picked.push(r as Row);
    }
    return picked;
  }, [selSettings.rowSelectionModel, selSettings.allRows, getId]) as Row[];

  const selectedCount = selectedRows.length;

  /* -------- legacy auto-merge (no checkboxSelection) --------
     Backward compatible: if the select grid DOES NOT use checkboxSelection,
     we auto-merge on the first selected id (old behavior).
     If it DOES use checkboxSelection, we skip auto-merge and require applySelection(). */
  useUpdateEffect(() => {
    if (!selectorOpen) return;

    // If using checkboxSelection, do not auto-merge.
    if ((selSettings as any)?.checkboxSelection) return;

    const model = selSettings.rowSelectionModel ?? [];
    if (!Array.isArray(model) || model.length === 0) return;

    const picked: Row[] = [];
    model.every((rid: GridRowId) => {
      const hit = selSettings.allRows?.find((r: any) => getId(r) === rid);
      if (hit) {
        picked.push(hit as Row);
        return false; // only the first one, same as old behavior
      }
      return true;
    });

    if (!picked.length) return;

    const result = mergeImpl(rows, picked, replaceId);

    let nextRows: Row[];
    let keepOpen = false;
    let newMessage: string | undefined;

    if (Array.isArray(result)) {
      nextRows = result;
    } else {
      nextRows = result.rows;
      newMessage = result.message;
      keepOpen = result.keepOpen ?? Boolean(newMessage);
    }

    setRows(nextRows);
    if (newMessage) setMessage(newMessage);
    if (!keepOpen) setSelectorOpen(false);

    selSettings.setRowSelectionModel?.([]); // clear selection
  }, [selSettings.rowSelectionModel]);

  /* -------- explicit apply for checkboxSelection flow -------- */
  const applySelection = (opts?: { keepOpen?: boolean }) => {
    if (!selectedRows.length) return;

    // Only respect replaceId if exactly one item is picked; otherwise append.
    const useReplaceId = selectedRows.length === 1 ? replaceId : null;
    const result = mergeImpl(rows, selectedRows, useReplaceId);

    let nextRows: Row[];
    let keepOpen = Boolean(opts?.keepOpen);
    let newMessage: string | undefined;

    if (Array.isArray(result)) {
      nextRows = result;
    } else {
      nextRows = result.rows;
      newMessage = result.message;
      keepOpen = keepOpen || result.keepOpen || Boolean(newMessage);
    }

    setRows(nextRows);
    if (newMessage) setMessage(newMessage);

    // Clear selection either way
    selSettings.setRowSelectionModel?.([]);

    if (!keepOpen) setSelectorOpen(false);
  };

  const clearSelection = () => {
    selSettings.setRowSelectionModel?.([]);
  };

  /* -------- display grid commit plumbing -------- */
  useLayoutEffect(() => {
    const processRowUpdateRef = displayTableSettings?.processRowUpdateRef;
    if (!processRowUpdateRef) return;

    const previousHandler = processRowUpdateRef.current;

    const handler = (updatedRow: Row, originalRow: Row) => {
      const extra = onCommitFunction ? onCommitFunction(updatedRow, originalRow) : {};
      const candidate = { ...updatedRow, ...extra };

      const committed = previousHandler
        ? previousHandler(candidate, originalRow)
        : candidate;

      setRows((prev) => {
        const safePrev = Array.isArray(prev) ? prev : [];
        const committedId = (committed as any)[rowId];

        let hit = false;

        const next = safePrev.map((r: any) => {
          if (r[rowId] === committedId) {
            hit = true;
            return { ...r, ...committed };
          }

          return r;
        });

        return hit ? next : [...next, committed];
      });

      return committed;
    };

    processRowUpdateRef.current = handler;

    return () => {
      if (processRowUpdateRef.current === handler) {
        processRowUpdateRef.current = previousHandler ?? null;
      }
    };
  }, [
    displayTableSettings?.processRowUpdateRef,
    setRows,
    rowId,
    onCommitFunction,
  ]);

  /* -------- public API -------- */
  return {
    open: selectorOpen,
    onClose: () => setSelectorOpen(false),
    openSelector: (id: string | number | null = null) => {
      setReplaceId(id);
      setSelectorOpen(true);
    },
    applySelection,
    selectedRows,
    selectedCount,
    clearSelection,
    columns: selColumns,
    tableSettings: selSettings,
    message,
    clearMessage: message !== undefined ? () => setMessage(undefined) : undefined,
  };
};